import * as Sentry from "@sentry/react";
import { createLogger } from "redux-logger";
import {
  Reducer,
  StoreEnhancer,
  combineReducers,
  configureStore
} from "@reduxjs/toolkit";
import { persistReducer, persistStore } from "redux-persist";
import storageImport from "redux-persist/lib/storage";
import { debug } from "@/utils/debug";

// Slices
import activeAccount from "./activeAccount/activeAccount.slice";
import auth from "./authorization/authorization.slice";
import dev from "./dev/dev.slice";
import header from "./header/header.slice";
import gisUpload from "./gisUpload/gisUpload.slice";
import map from "./map/map.slice";
import request from "./request/request.slice";
import websocket from "./websocket/websocket.slice";

// APIs
import { aiAssistantApi } from "./aiAssistant/aiAssistant.api";
import { authorizationApi } from "./authorization/authorization.api";
import { genericFetchApi } from "./genericFetch/genericFetch.api";
import { highlandPortalApi } from "./highlandPortal/highlandPortal.api";
import { logApi } from "./log/log.api";
import { mobileAppApi } from "./mobileApp/mobileApp.api";
import { portalCompaniesApi } from "./portalCompanies/portalCompanies.api";
import { portalPartnersApi } from "./portalPartners/portalPartners.api";
import { portalProjectsApi } from "./portalProjects/portalProjects.api";
import { portalProjectsGISApi } from "./portalProjects/portalProjectsGIS.api";
import { portalUsersApi } from "./portalUsers/portalUsers.api";
import { reportApi } from "./reports/reports.api";
import { userApi } from "./user/user.api";
import { gisUploadApi } from "./gisUpload/gisUpload.api";
import { GISAdminApi } from "./GISAdmin/GISAdmin.api";
import { portalApi } from "./api";
import "@/features/invitations/api/invitations.api";

// Utils
import { getApiEntities } from "@/utils/redux";

// Constants
import { IS_DEV } from "@/constants/Dev.constant";
import { TypedUseSelectorHook, useDispatch, useSelector } from "react-redux";

/** Default import from redux-logger is not always a middleware fn in browser ESM; createLogger() is. */
const logger = createLogger();

/** Default export can be wrapped ({ default }) in browser ESM interop; persist needs getItem(). */
const persistStorage =
  storageImport &&
  typeof (storageImport as { getItem?: unknown }).getItem === "function"
    ? storageImport
    : (storageImport as unknown as { default: typeof storageImport }).default;

const apiEntities = [
  userApi,
  authorizationApi,
  aiAssistantApi,
  genericFetchApi,
  logApi,
  reportApi,
  portalCompaniesApi,
  gisUploadApi,
  GISAdminApi,
  mobileAppApi,
  portalProjectsApi,
  portalProjectsGISApi,
  portalPartnersApi,
  highlandPortalApi,
  portalUsersApi,
  portalApi
];

const { reducers, middlewares } = getApiEntities(apiEntities);

const persistConfig = {
  key: "root",
  storage: persistStorage,
  whitelist: ["activeAccount", "auth", "dev"]
};

const appReducer = combineReducers({
  ...reducers,
  auth,
  dev,
  request,
  activeAccount,
  gisUpload,
  header,
  map,
  websocket
});

const rootReducer: Reducer<ReturnType<typeof appReducer>> = (state, action) => {
  if (action.type === "RESET_STATE") {
    debug.log("[reset state] RESET_STATE");
    return appReducer(undefined, action);
  }

  return appReducer(state, action);
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

const sentryReduxEnhancer = Sentry.createReduxEnhancer() as StoreEnhancer;

export const setupStore = (preloadedState?: Partial<RootState>) => {
  return configureStore({
    reducer: persistedReducer,
    devTools: IS_DEV,
    middleware: (getDefaultMiddleware) => {
      const defaultMiddlewares = getDefaultMiddleware({
        serializableCheck: {
          ignoredActions: ["persist/PERSIST"],
          warnAfter: 240
        }
      });

      if (IS_DEV) {
        return defaultMiddlewares.concat(logger).concat(middlewares);
      }

      return defaultMiddlewares.concat(middlewares);
    },
    enhancers: (getDefaultEnhancers) => {
      return getDefaultEnhancers().concat(sentryReduxEnhancer);
    },
    preloadedState: preloadedState as RootState
  });
};

export const store = setupStore();

export const persistor = persistStore(store);

export type RootState = ReturnType<typeof persistedReducer>;
export type AppStore = ReturnType<typeof setupStore>;
export type AppDispatch = AppStore["dispatch"];

export const useAppDispatch: () => AppDispatch = useDispatch;
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
